import React from 'react';
import { X, Globe, Network } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';

export interface IxpFacilityProperties {
  id: number;
  peeringdb_id: number;
  type: 'ixp' | 'facility';
  name: string;
  city: string | null;
  state: string | null;
  networks_count: number;
  website: string | null;
}

interface IxpFacilityDetailProps {
  facility: IxpFacilityProperties;
  onClose: () => void;
}

const TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  ixp: { label: 'Internet Exchange Point', color: '#10b981' },
  facility: { label: 'Carrier-Neutral Colocation', color: '#6ee7b7' },
};

export const IxpFacilityDetail: React.FC<IxpFacilityDetailProps> = ({ facility, onClose }) => {
  const { theme } = useTheme();
  const config = TYPE_CONFIG[facility.type] || { label: facility.type, color: '#6b7280', description: '' };
  const location = [facility.city, facility.state].filter(Boolean).join(', ');
  const cleanUrl = facility.website?.replace(/^https?:\/\//, '').replace(/\/$/, '') || null;

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Network size={16} style={{ color: config.color, flexShrink: 0 }} />
            <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>
              {facility.name}
            </h2>
          </div>
          <div style={{
            marginTop: '2px', marginLeft: '24px', display: 'inline-block',
            padding: '2px 8px', fontSize: '10px', fontWeight: 600,
            background: config.color + '20', color: config.color,
            borderRadius: '4px',
          }}>
            {config.label}
          </div>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* Key metric — network count */}
        {facility.networks_count > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: '11px', color: theme.textSecondary }}>
                {facility.type === 'ixp' ? 'Peering Networks' : 'Connected Networks'}
              </span>
              <span style={{
                fontFamily: designTokens.typography.fontFamily.mono,
                fontSize: '18px', fontWeight: 600, color: config.color,
              }}>{facility.networks_count}</span>
            </div>
            <div style={{ fontSize: '10px', color: theme.textTertiary, marginTop: '2px' }}>
              {facility.type === 'ixp'
                ? 'Networks exchanging traffic at this IX'
                : 'Carriers / networks with presence in this facility'}
            </div>
          </div>
        )}

        {/* Location */}
        <Section title="Location">
          {location && <Field label="Location" value={location} />}
        </Section>

        {/* Website */}
        {facility.website && (
          <Section title="Website">
            <a
              href={facility.website.startsWith('http') ? facility.website : `https://${facility.website}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                fontSize: '11px', color: config.color, textDecoration: 'underline',
              }}
            >
              <Globe size={11} />
              {cleanUrl}
            </a>
          </Section>
        )}

        {/* What this means */}
        <Section title="What This Means for BTM">
          <div style={{
            padding: '8px 10px', borderRadius: '6px',
            background: `${config.color}10`, border: `1px solid ${config.color}40`,
          }}>
            <div style={{ fontSize: '11px', color: theme.text, lineHeight: 1.55 }}>
              {facility.type === 'ixp' ? (
                <>
                  An IXP within 100 miles means sub-2ms peering latency — sufficient for most hyperscaler workloads.
                  Direct peering at an IXP avoids upstream transit costs and adds a redundant path independent of any single carrier.
                </>
              ) : (
                <>
                  A nearby colo facility provides a meet-me point for cross-connecting to multiple carriers without building laterals to each one.
                  For BTM sites, the nearest colo is typically where you&apos;d hand off to the carrier backbone.
                </>
              )}
            </div>
          </div>
        </Section>

        {/* PeeringDB link */}
        <Section title="Data Source">
          <Field label="Source" value="PeeringDB" />
          <div style={{ marginTop: '6px' }}>
            <a
              href={`https://www.peeringdb.com/${facility.type === 'ixp' ? 'ix' : 'fac'}/${facility.peeringdb_id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '10px', color: config.color, textDecoration: 'underline' }}
            >
              View on PeeringDB
            </a>
          </div>
          <div style={{ fontSize: '10px', color: theme.textTertiary, lineHeight: 1.5, marginTop: '8px' }}>
            PeeringDB is a community-maintained database of network interconnection data.
            Network counts and facility presence are self-reported by participating networks.
          </div>
        </Section>
      </div>
    </div>
  );
};
